import React, { useCallback, useEffect } from "react";
import { ModalConfirm } from "collections/Modal";
import { useDeviceCallState } from "../View/hooks";
import {
  useFormikContext,
  FormikConfig,
  FormikHelpers
} from "collections/Form";
import {
  createStandardAction,
  createAction,
  ActionType,
  getType
} from "typesafe-actions";
import { useTypesafeActions } from "store";
import { useTranslation } from "utilities/i18n";

type Steps = "Interrupt" | "Confirm" | "Refire";
type State = {
  allowDefaultSubmit: boolean;
  inCall: boolean;
  step: Steps | null;
};
const initialState: State = {
  allowDefaultSubmit: true,
  inCall: false,
  step: null
};
const Actions = {
  updateCallState: createStandardAction("UpdateCallState")<boolean>(),
  interrupt: createAction<Steps>("Interrupt"),
  confirm: createAction<Steps>("Confirm"),
  refire: createAction<Steps>("Refire"),
  reset: createAction("Reset")
};
type ActionTypes = typeof Actions;

function reducer(state: State, a: ActionType<ActionTypes>): State {
  switch (a.type) {
    case getType(Actions.updateCallState):
      return {
        ...state,
        allowDefaultSubmit: !a.payload,
        inCall: a.payload
      };
    case getType(Actions.reset):
      return {
        ...initialState,
        inCall: state.inCall,
        allowDefaultSubmit: !state.inCall
      };
    default:
      return {
        ...state,
        step: a.type
      };
  }
}

type SubmitFn<Values> = FormikConfig<Values>["onSubmit"];
type HookReturn<Values> = {
  activeCallInterrupt: (original: SubmitFn<Values>) => SubmitFn<Values>;
  actions: typeof Actions;
  openConfirm: boolean;
};

export function useActiveCallModalActions<Values>(
  id?: string
): HookReturn<Values> {
  const [{ allowDefaultSubmit, step }, actions] = useTypesafeActions<
    State,
    typeof Actions
  >(reducer, initialState, Actions);
  const { reset, updateCallState, interrupt, refire } = actions;
  const { inCall } = useDeviceCallState(id);
  const activeCallInterrupt = useCallback(
    (original: FormikConfig<Values>["onSubmit"]) => async (
      v: Values,
      h: FormikHelpers<Values>
    ) => {
      if (allowDefaultSubmit) {
        return await original(v, h);
      }
      if (step === "Confirm") {
        refire();
        try {
          return await original(v, h);
        } finally {
          reset();
        }
      }
      if (step === null) {
        interrupt();
        h.setSubmitting(false);
      }
      return Promise.resolve();
    },
    [allowDefaultSubmit, reset, step, interrupt, refire]
  );
  useEffect(() => {
    updateCallState(inCall);
  }, [updateCallState, inCall]);
  return {
    activeCallInterrupt,
    actions,
    openConfirm: step === "Interrupt"
  };
}

export const ActiveCallConfirmModal: React.FC<{
  openConfirm: boolean;
  actions: ActionTypes;
}> = ({ openConfirm, actions: { reset, confirm } }) => {
  const { submitForm } = useFormikContext();
  const { t } = useTranslation();
  const cancel = useCallback(() => {
    reset();
  }, [reset]);
  const confirmClick = useCallback(() => {
    confirm();
    submitForm().then();
  }, [submitForm, confirm]);

  return (
    <ModalConfirm
      title={t("ACTIVE_CALL")}
      onConfirm={confirmClick}
      onCancel={cancel}
      open={openConfirm}
      message={t("CONFIRM_ACTIVE_CALL")}
    />
  );
};
export default ActiveCallConfirmModal;
